import * as XLSX from 'xlsx';

export const generateSettlementExcel = (settlement, trips, advances = []) => {
    const tripsList = Array.isArray(trips) ? trips : [];
    const advancesList = Array.isArray(advances) ? advances : [];
    const currency = tripsList.length > 0
        ? (tripsList[0].provider_currency_code || tripsList[0].local_currency_code || settlement.currency_code || 'CLP')
        : (settlement.currency_code || 'CLP');

    const totalDescuentosNeto = advancesList.reduce((sum, a) => sum + (parseFloat(a.amount) || 0), 0);
    const sumDescuentosPositivos = advancesList.reduce((sum, a) => {
        const amt = parseFloat(a.amount) || 0;
        return amt > 0 ? sum + amt : sum;
    }, 0);
    const sumAbonosPositivos = advancesList.reduce((sum, a) => {
        const amt = parseFloat(a.amount) || 0;
        return amt < 0 ? sum + Math.abs(amt) : sum;
    }, 0);

    const totalLiquido = settlement.total_amount;
    const totalServicios = totalLiquido + totalDescuentosNeto;

    // 1. Preparar datos de la carátula (Resumen)
    const summaryData = [
        ["LIQUIDACIÓN DE SERVICIOS - Transportes Emilia"],
        ["Nº Liquidación:", `LIQ-${String(settlement.id).padStart(5, '0')}`],
        ["Fecha Emisión:", settlement.created_at ? new Date(settlement.created_at).toLocaleDateString('es-CL') : ""],
        ["Conductor:", settlement.driver?.full_name?.toUpperCase() || ""],
        ["RUT Conductor:", settlement.driver?.rut || ""],
        ["Periodo:", `${settlement.period_start} al ${settlement.period_end}`],
        ["Moneda:", currency],
        [""],
        ["RESUMEN FINANCIERO"],
        ["Total Producción / Servicios:", totalServicios],
        ["Total Descuentos / Penalizaciones:", -sumDescuentosPositivos],
        ...(sumAbonosPositivos > 0 ? [["Total Abonos / Reintegros Booking:", sumAbonosPositivos]] : []),
        ["TOTAL LÍQUIDO A RECIBIR:", totalLiquido],
        ["Total Viajes:", settlement.total_trips],
        ["Estado Pago:", settlement.is_paid ? "PAGADA" : "PENDIENTE"],
        ["Nº Factura:", settlement.invoice_number || "S/N"],
        [""],
        ["DATOS BANCARIOS"],
        ["Banco:", settlement.driver?.bank?.name || ""],
        ["Tipo:", (() => {
            const rawAcc = (settlement.driver?.account_type || "").trim().toUpperCase();
            if (rawAcc === "CORRIENTE" || rawAcc === "CUENTA CORRIENTE") return "Cuenta Corriente";
            if (rawAcc === "VISTA" || rawAcc === "CUENTA VISTA" || rawAcc === "RUT" || rawAcc === "CUENTA RUT") return "Cuenta Vista";
            if (rawAcc === "AHORRO" || rawAcc === "CUENTA AHORRO") return "Cuenta Ahorro";
            return settlement.driver?.account_type || "";
        })()],
        ["Número:", settlement.driver?.account_number || ""],
        ["RUT Titular:", settlement.driver?.account_rut || ""],
        [""]
    ];

    // 2. Preparar datos del detalle (Tabla)
    const detailHeader = ["ID Solicitud", "Fecha", "Cliente", "Pasajero", "Origen", "Destino", "Producción", "A Pagar"];
    
    let sumProduccion = 0;
    let sumAPagar = 0;

    const detailRows = tripsList.map(t => {
        const waitCosto = parseFloat(t.waiting_time_provider_cost) || 0;
        const prod = (currency !== 'CLP' ? (t.provider_cost_local || 0) : (t.provider_cost_base || 0)) + waitCosto;
        
        let aPagar = (t.driver_earnings !== null && t.driver_earnings !== undefined && t.driver_earnings > 0)
            ? t.driver_earnings
            : (t.provider_cost_local || t.provider_cost_base || 0);

        const extraServices = Array.isArray(t.extra_services) ? t.extra_services : [];
        const reimbursable = extraServices.find(s => s && s.id === 'reimbursable_expense');
        if (reimbursable) {
            aPagar += parseFloat(reimbursable.cost) || 0;
        }

        sumProduccion += prod;
        sumAPagar += aPagar;

        return [
            t.id,
            t.scheduled_time ? new Date(t.scheduled_time).toLocaleDateString('es-CL') : "",
            t.client?.name || "",
            t.passenger_name || "",
            t.pickup_address || "",
            t.dropoff_address || "",
            prod,
            aPagar
        ];
    });

    // Agregar fila de totales al final
    detailRows.push([
        "TOTALES",
        "",
        "",
        "",
        "",
        "",
        sumProduccion,
        sumAPagar
    ]);

    // 3. Preparar datos de ajustes (descuentos, penalizaciones y abonos)
    const advHeader = ["Fecha", "Tipo / Concepto", "Detalle / Referencia (OS / Viaje)", "Monto Ajuste"];
    const advRows = advancesList.map(a => {
        const rawAmt = parseFloat(a.amount) || 0;
        const isCredit = rawAmt < 0;
        const notesStr = a.notes || (isCredit ? "Abono / Reintegro Booking" : "Anticipo de caja");
        const isPenalty = !isCredit && (notesStr.toLowerCase().includes('disputa') || notesStr.toLowerCase().includes('booking') || notesStr.toLowerCase().includes('penalizaci'));
        const tipo = isCredit ? "Abono / Reintegro Booking" : (isPenalty ? "Penalización Booking" : "Anticipo de Caja");
        
        let fStr = "";
        if (a.created_at) fStr = new Date(a.created_at).toLocaleDateString('es-CL');

        return [
            fStr,
            tipo,
            notesStr,
            -rawAmt
        ];
    });
    if (advRows.length > 0) {
        advRows.push(["TOTAL AJUSTES NETOS", "", "", -totalDescuentosNeto]);
    }

    // 4. Crear el libro y las hojas
    const wb = XLSX.utils.book_new();
    
    // Hoja Resumen
    const wsSummary = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(wb, wsSummary, "Resumen");

    // Hoja Detalle
    const wsDetail = XLSX.utils.aoa_to_sheet([detailHeader, ...detailRows]);
    XLSX.utils.book_append_sheet(wb, wsDetail, "Detalle de Servicios");

    // Hoja Ajustes / Descuentos (si existen)
    if (advancesList.length > 0) {
        const wsAdv = XLSX.utils.aoa_to_sheet([advHeader, ...advRows]);
        XLSX.utils.book_append_sheet(wb, wsAdv, sumAbonosPositivos > 0 ? "Ajustes y Abonos" : "Descuentos y Penalizaciones");
    }

    // 5. Descargar
    const fileName = `Liquidacion_LIQ-${String(settlement.id).padStart(5, '0')}_${(settlement.driver?.full_name || 'Chofer').replace(/\s+/g, '_')}.xlsx`;
    XLSX.writeFile(wb, fileName);
};
